/**
 * Marks real pending operations, and nothing else.
 *
 * Every indicator here is tied to a promise the theme already created for work it is already
 * doing. Horizon's cart and variant events carry that promise on the event itself, so this file
 * never starts a request, never guesses how long one will take, and never shows progress for an
 * operation that is not running. Nothing is slowed down to make a spinner look worthwhile: there
 * is no minimum display time, and a fast response simply never reveals one.
 *
 * ## The 350ms threshold
 *
 * Below it, an indicator is a flash — visible for less than a blink, which reads as a rendering
 * fault rather than as progress. So the pending attribute is set on a timer and cancelled if the
 * work finishes first. This delays the indicator, never the operation.
 *
 * ## The ceiling
 *
 * Every indicator is raced against a timeout. A promise that never settles — a dropped connection,
 * a response the page never receives — would otherwise leave a mark spinning forever, which claims
 * work is still happening when nothing is. The ceiling ends the claim; it does not cancel the
 * request, because the request is not this file's to cancel.
 */

const SPINNER_DELAY_MS = 350;

/** Longest an indicator may claim work is in progress before it stops claiming it. */
const CEILING_MS = 20000;

const PENDING = 'data-timecard-pending';

/**
 * Shows the indicator on `element` while `promise` is outstanding.
 *
 * @param {Element | null | undefined} element
 * @param {Promise<unknown> | null | undefined} promise
 */
export function markPending(element, promise) {
  if (!(element instanceof HTMLElement) || !promise?.then) return;

  /*
   * Each start gets a generation number, and only the newest one may clear the indicator.
   *
   * Choosing colour A and then colour B before A has finished is ordinary behaviour, and A is
   * usually ABORTED when B starts — so A's promise settles first, while B is still in flight. An
   * earlier version simply ignored the second start, which meant A's ending cleared the spinner
   * and B loaded with no indication at all. Tracking the generation makes the stale ending a
   * no-op and leaves the live one in charge.
   */
  const generation = Number(element.dataset.timecardPendingGeneration ?? '0') + 1;
  element.dataset.timecardPendingGeneration = String(generation);

  const isCurrent = () => element.dataset.timecardPendingGeneration === String(generation);

  // The previous generation's reveal timer, cancelled so it cannot re-arm after this one clears.
  window.clearTimeout(Number(element.dataset.timecardPendingReveal ?? '0'));

  const reveal = window.setTimeout(() => {
    if (!isCurrent()) return;
    element.setAttribute(PENDING, 'true');

    /*
     * `aria-busy` is set with the indicator, not before it.
     *
     * It says "this region is mid-update, do not announce it yet", which is true only while there
     * is something to wait for. Setting it the instant work starts would make every fast operation
     * announce a wait that never happened; setting it with the visible mark keeps the two senses
     * telling the same story.
     *
     * An element that was ALREADY busy stays that way and is left alone. The attribute may belong
     * to a native component that is mid-update for its own reasons, and clearing someone else's
     * flag would announce a region as settled while its owner still considers it in flight. Only a
     * flag this file set is a flag this file may remove.
     */
    if (!element.hasAttribute('aria-busy')) {
      element.setAttribute('aria-busy', 'true');
      element.dataset.timecardPendingAria = '1';
    }
  }, SPINNER_DELAY_MS);
  element.dataset.timecardPendingReveal = String(reveal);

  let ceiling = 0;
  const ceilingReached = new Promise((resolve) => {
    ceiling = window.setTimeout(resolve, CEILING_MS);
  });

  const clear = () => {
    window.clearTimeout(ceiling);

    // A superseded operation ending — including an abort — must not take the live one's indicator.
    if (!isCurrent()) return;

    window.clearTimeout(reveal);
    element.removeAttribute(PENDING);

    if (element.dataset.timecardPendingAria === '1') {
      element.removeAttribute('aria-busy');
      delete element.dataset.timecardPendingAria;
    }

    delete element.dataset.timecardPendingReveal;
  };

  // `race` rather than `finally`: a rejection is still an ending, and so is the ceiling.
  Promise.race([Promise.resolve(promise).catch(() => undefined), ceilingReached]).then(clear, clear);
}

/**
 * Adding to cart.
 *
 * Horizon dispatches this from the product form with the add's own promise attached, so the
 * indicator starts and stops with the real request. The button is not disabled: the form already
 * queues concurrent adds, and disabling it here would fight that queue and drop the second click
 * instead of running it.
 */
document.addEventListener('shopify:cart:lines-update', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  /*
   * Only an add that came from a product form. The same event is dispatched for quantity changes
   * and removals inside the cart, which have their own affordances and are not this button's work.
   */
  const button = target.querySelector('add-to-cart-component button, [data-timecard-newsletter-submit]');
  if (!button) return;

  markPending(button, /** @type {any} */ (event).promise);
});

/**
 * Choosing a colour or size.
 *
 * What is actually pending is the price and the pickup availability. Both are showing the PREVIOUS
 * variant's answer until the section comes back, and a stale price is the one thing on this page a
 * visitor could act on while it is wrong.
 *
 * The gallery is deliberately not marked from this event. For a colour change the picker already
 * dims it — `data-timecard-gallery-swapping`, set in `variant-picker.js` — so a spinner on top was
 * a second indicator for one operation. For a size change the gallery does not change at all, and
 * the old code marked it anyway: a spinner over images that were never going to be replaced. The
 * gallery gets an indicator further down, and only when images are genuinely loading.
 *
 * ## Scope
 *
 * `closest` walks outward to the nearest of these, so a swatch inside a product card resolves to
 * that card and a picker on a product page resolves to the product information. Scoping to the
 * whole section instead — which is what this used to do — finds the FIRST price in a collection
 * grid, which on a card swatch is some other product's.
 */
const VARIANT_SCOPE = 'product-card, .product-information, .shopify-section, dialog';

document.addEventListener('shopify:product:select', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const promise = /** @type {any} */ (event).promise;
  if (!promise?.then) return;

  const scope = target.closest(VARIANT_SCOPE);
  if (!scope) return;

  markPending(scope.querySelector('product-price'), promise);

  /*
   * Pickup availability, but only when it is on screen. `local-pickup` hides itself for a variant
   * that cannot be picked up, and marking a hidden element busy claims work on something the
   * visitor cannot see.
   */
  const pickup = scope.querySelector('local-pickup');
  if (pickup instanceof HTMLElement && !pickup.hasAttribute('hidden')) markPending(pickup, promise);

  /*
   * The gallery, once the section has actually come back.
   *
   * At this point the new markup is in place, so the images either are already decoded — a colour
   * the visitor has looked at before, served from cache — or they are genuinely fetching. Only the
   * second case gets an indicator, and only for as long as the images take. If nothing is loading,
   * nothing is marked: an instant swap should look instant.
   */
  Promise.resolve(promise)
    .catch(() => undefined)
    .then(() => markLoadingImages(document.contains(scope) ? scope : document));
});

/**
 * Marks the individual gallery tiles whose image is genuinely being fetched.
 *
 * ## Why this is per-tile and heavily filtered
 *
 * A product gallery is not the handful of pictures on screen. The live DOM carries around thirty
 * `<img>` for six displayed ones: a desktop set and a mobile set, only one of which is laid out,
 * plus a full zoom-dialog copy. Most of them are `loading="lazy"` and offscreen, so the browser has
 * not asked for them and never will unless the visitor scrolls or opens the zoom.
 *
 * `img.complete` is false for every one of those. An indicator waiting on all of them waits for
 * requests that were never made — it would sit there until the 20s ceiling cut it off, claiming the
 * gallery was loading long after it had finished. That is the fabricated wait this file exists to
 * avoid, so the filter is deliberately strict:
 *
 *   `currentSrc` is set   the browser has chosen a source and started fetching. A lazy image that
 *                         has not been requested has an empty `currentSrc`, which is the only
 *                         reliable way to tell "still arriving" from "never asked for".
 *   not `complete`        it has not finished already.
 *   the tile is laid out  a zero-sized rect is the hidden half of the desktop/mobile pair.
 *   not a zoom copy       `.dialog-zoomed-gallery` and dialogs are a second set of the same images.
 *
 * Anything that passes all four is a picture the visitor is actually waiting to see. If nothing
 * passes, nothing is marked — an instant swap should look instant.
 *
 * @param {ParentNode} scope
 */
function markLoadingImages(scope) {
  const gallery = scope.querySelector('media-gallery');
  if (!(gallery instanceof HTMLElement)) return;

  for (const tile of gallery.querySelectorAll('.product-media-container')) {
    if (!(tile instanceof HTMLElement)) continue;
    if (tile.closest('.dialog-zoomed-gallery, dialog, zoom-dialog')) continue;

    const rect = tile.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;

    const img = tile.querySelector('img');
    if (!(img instanceof HTMLImageElement)) continue;
    if (img.complete || !img.currentSrc) continue;

    markPending(tile, imageSettled(img));
  }
}

/**
 * Resolves when an image stops loading, either way.
 *
 * The `complete` re-check after attaching is not belt and braces: an image can finish between the
 * filter above and the listener below, and `load` has already fired by then. Without the re-check
 * that tile would keep its indicator until the ceiling.
 *
 * @param {HTMLImageElement} img
 * @returns {Promise<void>}
 */
function imageSettled(img) {
  return new Promise((resolve) => {
    const done = () => {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      resolve();
    };

    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });

    if (img.complete) done();
  });
}

/**
 * Filtering, sorting and searching.
 *
 * `facets.js` hands its section-render promise to both of these events, and `predictive-search.js`
 * hands over its own fetch. The results themselves are what is stale, so the indicator goes on the
 * section that contains them rather than on the control that was clicked — a filter checkbox is
 * not the thing the visitor is waiting for.
 *
 * The section element survives the re-render; the grid inside it does not. Marking the grid would
 * put the attribute on a node that is about to be replaced, and the clear would land on a detached
 * element.
 */
for (const name of ['shopify:search:update', 'shopify:collection:update']) {
  document.addEventListener(name, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const promise = /** @type {any} */ (event).promise;
    if (!promise?.then) return;

    // Predictive search owns a results container of its own and is not a section re-render.
    const predictive = target.closest('predictive-search, search-modal');
    if (predictive) {
      markPending(predictive.querySelector('[ref="predictiveSearchResults"]') ?? predictive, promise);
      return;
    }

    markPending(target.closest('.shopify-section'), promise);
  });
}
